import React, { createContext, ReactChild, ReactChildren, useEffect, useMemo, useState } from 'react'
import fetchFactory from '../../../../utils/fetchFactory'
import { Visite } from '../utils/edlTypes'
import { propertyType } from '../../../../enums/contractsEnum'
import { ContractsModel } from '../../../../types/contracts/contractsModel'
import { phaseEnum } from '../../../../enums/phaseEnum'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../reducers'
import { LEAVE_DEPOSIT_CATEGORY } from '../../../requests/list/RequestCard'
import { RequestItemModel } from '../../../requests/requestsModel'
import localStorageEnum from '../../../../enums/localStorageEnum'
import { getLastRequestUser } from '../utils/demandeResiApi'
import { ACCESS_TOKEN_STORAGE_KEY } from '@okta/okta-auth-js'
import { useCookies } from 'react-cookie'
import { useIntl } from 'react-intl'
import { displaySnackbar } from '../../../reducers/snackBar/actions'

export interface EDLContextProps {
	offre: any
	refresh: () => void
	idRequete?: string
	requete?: RequestItemModel
	bail?: ContractsModel
	idVisiteEdle?: string
	idVisiteEdls?: string
	shouldDisplayEdle: boolean
	shouldDisplayEdls: boolean
	loading: boolean
	isRdvEdlePlanned: boolean
	isRdvEdlsPlanned: boolean
	demandeResidentielId: string
	dateEffetBail: Date | null
	dateCongesSouhaitee: Date | null
}

export const EDLContext = createContext<EDLContextProps>({
	offre: {},
	refresh: () => {} ,
	idRequete: undefined,
	requete: undefined,
	bail: undefined,
	idVisiteEdle: undefined,
	idVisiteEdls: undefined,
	shouldDisplayEdle: false,
	shouldDisplayEdls: false,
	loading: false,
	isRdvEdlePlanned: false,
	isRdvEdlsPlanned: false,
	demandeResidentielId: '',
	dateEffetBail: null,
	dateCongesSouhaitee: null,
})

interface EDLProviderProps {
	children: ReactChild | ReactChildren
}


interface DemandeResi {
	demande_residentiel_id: string
}

export const EDLProvider: React.FC<EDLProviderProps> = ({ children }) => {
	const ERREUR_CHARGEMENT_OFFRE = 'erreurChargementOffreEdl'
	const dispatch = useDispatch()
	const intl = useIntl()
	const requests: RequestItemModel[] = useSelector((state: RootState) => state.requests)
	const [incomingVisitsEdle, setIncomingVisitsEdle] = useState<Visite[]>([])
	const [incomingVisitsEdls, setIncomingVisitsEdls] = useState<Visite[]>([])
	const [loading, setLoading] = useState<boolean>(true)
	const [offre, setOffre] = useState<any>()
	const contracts = useSelector((state: RootState) => state.contracts)
	const [cookies] = useCookies([ACCESS_TOKEN_STORAGE_KEY])
	const accessTokenFromCookies = cookies[ACCESS_TOKEN_STORAGE_KEY]
	const [demandeResidentielId, setDemandeResidentielId] = useState<string>(localStorage.getItem(localStorageEnum.derniereDemande) ?? '')

	const contratsEnExploitation = (contracts as ContractsModel[]).filter((contract) => contract.phase === phaseEnum.exploitation)
		.sort((a, b) => {
			if (b.dateEffet && a.dateEffet) {
				return b.dateEffet.getTime() - a.dateEffet.getTime()
			}
			return 0
		})

	useEffect(() => {
		refreshAll()
		if (!localStorage.getItem(localStorageEnum.derniereDemande) || localStorage.getItem(localStorageEnum.derniereDemande)!.length === 0) {
			getLastRequestUser(accessTokenFromCookies.value)
				.then((response: any) => {
					const demandeResi: DemandeResi = response as DemandeResi
					if (demandeResi.demande_residentiel_id) {
						setDemandeResidentielId(demandeResi.demande_residentiel_id)
					}
				})
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[])

	const refreshAll = () => {
		setLoading(true)
		fetchFactory('/api/ec_residence/visits/getVisitsUtilisateur?passed=false')
			.then((visites: any) => {
				setIncomingVisitsEdle(visites.visites.filter((it: Visite) => it.isEdle))
				setIncomingVisitsEdls(visites.visites.filter((it: Visite) => it.isEdls))
				if (contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment).length) {
					refreshOffre(contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment)[0].idLot)
				}
			})
			.finally(() => setLoading(false))
	}

	const refreshOffre = (idLot: string) => {
		fetchFactory(`/api/ec_residence/dossier/getInfosOffre?idLot=${idLot}`)
			.then((offre: any) => setOffre(offre))
			.catch(() => {
				// DR-4835 Sans offre, la modale EDLE n'est pas montée et le bouton reste sans effet.
				// On n'avertit que si le bloc EDLE est affiché, sinon le message serait hors contexte.
				if (!afficherEDLE) {
					return
				}

				dispatch(displaySnackbar({
					id: ERREUR_CHARGEMENT_OFFRE,
					open: true,
					hideIcon: true,
					message: intl.formatMessage({ id: 'preparezArrivee.etatLieuxRemiseCles.erreurChargementOffre' })
				}))
			})
	}

	const afficherEDLE = useMemo(() => contratsEnExploitation.filter(contract => {
		return contract.type === propertyType.apartment && (contract.dateEffet && new Date(contract.dateEffet) >= new Date())
	}).length > 0, [contratsEnExploitation])

	const afficherEDLS = useMemo(() => {
		const contratEnExploit = contratsEnExploitation.find(contract => contract.type === propertyType.apartment)
		return !!contratEnExploit && requests.some(req => req.category === LEAVE_DEPOSIT_CATEGORY && req.bail_id === contratEnExploit.id)
	}, [requests, contratsEnExploitation])

	const isRdvEdlePlanned = useMemo(() => {
		const contract = contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment)[0]
		return incomingVisitsEdle.length > 0 || (!!contract && !!contract.jour1 && !!contract.heure1)
	}, [contratsEnExploitation, incomingVisitsEdle])

	const isRdvEdlsPlanned = useMemo(() => {
		const contratEnExploit = contratsEnExploitation.find(contract => contract.type === propertyType.apartment)
		const resiliationRequest = contratEnExploit
			? requests.find(it => it.category === LEAVE_DEPOSIT_CATEGORY && it.bail_id === contratEnExploit.id)
			: undefined
		return incomingVisitsEdls.length > 0 || (!!resiliationRequest && !!resiliationRequest.jour1 && !!resiliationRequest.heure1)
	}, [incomingVisitsEdls, requests, contratsEnExploitation])

	return (
		<EDLContext.Provider
			value={{
				offre,
				refresh: refreshAll,
				idVisiteEdle: incomingVisitsEdle[0]?.crmId ,
				idVisiteEdls: incomingVisitsEdls[0]?.crmId ,
				idRequete: requests.filter(it => it.category === LEAVE_DEPOSIT_CATEGORY)[0]?.id,
				requete: requests.filter(it => it.category === LEAVE_DEPOSIT_CATEGORY)[0],
				bail: contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment)[0],
				dateEffetBail: contratsEnExploitation.filter((contract) => contract.type === propertyType.apartment)[0]?.dateEffet,
				dateCongesSouhaitee: requests.filter(it => it.category === LEAVE_DEPOSIT_CATEGORY)[0]?.dateDepartSouhaitee ?? null,
				shouldDisplayEdle: afficherEDLE,
				shouldDisplayEdls: afficherEDLS,
				loading,
				isRdvEdlePlanned: isRdvEdlePlanned,
				isRdvEdlsPlanned: isRdvEdlsPlanned,
				demandeResidentielId,
			}}
		>
			{children}
		</EDLContext.Provider>
	)
}